import React, { useCallback, useEffect, useState } from "react";
import { FiCheck, FiChevronDown, FiChevronUp, FiX } from "react-icons/fi";
import Header from "../components/layout/Header";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Select from "../components/ui/Select";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import { timesheetsApi, Timesheet } from "../api/timesheets";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";

const STATUS_VARIANT: Record<
  Timesheet["status"],
  "warning" | "success" | "danger" | "neutral"
> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

const FILTER_OPTIONS = [
  { value: "pending", label: "En attente" },
  { value: "", label: "Toutes" },
  { value: "approved", label: "Validées" },
  { value: "rejected", label: "Refusées" },
  { value: "cancelled", label: "Annulées" },
];

const TimesheetsRhPage: React.FC = () => {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [reviewTarget, setReviewTarget] = useState<Timesheet | null>(null);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject">(
    "approve",
  );
  const [rhComment, setRhComment] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await timesheetsApi.list({
        status: statusFilter || undefined,
        per_page: 100,
      });
      setTimesheets(res.data.data?.timesheets ?? []);
      setPendingCount(res.data.data?.pending_count ?? 0);
    } catch {
      toast.error("Impossible de charger les feuilles de temps");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openReview = (sheet: Timesheet, action: "approve" | "reject") => {
    setReviewTarget(sheet);
    setReviewAction(action);
    setRhComment("");
  };

  const handleReview = async () => {
    if (!reviewTarget) return;
    setReviewing(true);
    try {
      await timesheetsApi.review(reviewTarget.id, {
        action: reviewAction,
        rh_comment: rhComment.trim() || undefined,
      });
      toast.success(
        reviewAction === "approve" ? "Feuille validée" : "Feuille refusée",
      );
      setReviewTarget(null);
      await loadData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Échec du traitement";
      toast.error(message);
    } finally {
      setReviewing(false);
    }
  };

  const pendingHours = timesheets
    .filter((t) => t.status === "pending")
    .reduce((sum, t) => sum + t.total_hours, 0);

  return (
    <div>
      <Header
        title="Feuilles de temps"
        subtitle="Validez les déclarations horaires de vos collaborateurs"
      />

      <div style={{ padding: "2rem", maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            {pendingCount > 0 && (
              <Badge variant="warning">{pendingCount} en attente</Badge>
            )}
            {statusFilter === "pending" && pendingHours > 0 && (
              <span
                style={{
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-secondary)",
                }}
              >
                {pendingHours.toFixed(2)} h à valider
              </span>
            )}
          </div>
          <div style={{ minWidth: 220 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={FILTER_OPTIONS}
            />
          </div>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "3rem",
            }}
          >
            <LoadingSpinner size={36} />
          </div>
        ) : timesheets.length === 0 ? (
          <Card padding="2rem">
            <EmptyState
              title="Aucune feuille de temps"
              description={
                statusFilter === "pending"
                  ? "Aucune déclaration en attente."
                  : "Aucune feuille ne correspond à ce filtre."
              }
            />
          </Card>
        ) : (
          <Card padding="0">
            {timesheets.map((sheet, index) => (
              <div
                key={sheet.id}
                style={{
                  borderTop:
                    index > 0
                      ? "1px solid var(--color-border-light)"
                      : undefined,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr 80px 100px 120px",
                    gap: "0.5rem",
                    alignItems: "center",
                    padding: "1rem 1.25rem",
                  }}
                >
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                    onClick={() =>
                      setExpandedId(expandedId === sheet.id ? null : sheet.id)
                    }
                  >
                    {expandedId === sheet.id ? (
                      <FiChevronUp size={16} />
                    ) : (
                      <FiChevronDown size={16} />
                    )}
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 500,
                          fontSize: "var(--font-size-sm)",
                        }}
                      >
                        {sheet.employee_name || sheet.user_name}
                      </p>
                      <p
                        style={{
                          margin: "0.1rem 0 0",
                          fontSize: "var(--font-size-xs)",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        {sheet.week_label}
                      </p>
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-text-muted)",
                    }}
                  >
                    {format(new Date(sheet.created_at), "d MMM yyyy", {
                      locale: fr,
                    })}
                  </span>
                  <span
                    style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}
                  >
                    {sheet.total_hours} h
                  </span>
                  <Badge variant={STATUS_VARIANT[sheet.status]} size="sm">
                    {sheet.status_label}
                  </Badge>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "0.35rem",
                    }}
                  >
                    {sheet.status === "pending" ? (
                      <>
                        <button
                          type="button"
                          title="Valider"
                          onClick={() => openReview(sheet, "approve")}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--color-success)",
                            cursor: "pointer",
                          }}
                        >
                          <FiCheck size={18} />
                        </button>
                        <button
                          type="button"
                          title="Refuser"
                          onClick={() => openReview(sheet, "reject")}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--color-danger)",
                            cursor: "pointer",
                          }}
                        >
                          <FiX size={18} />
                        </button>
                      </>
                    ) : (
                      <span
                        style={{
                          fontSize: "var(--font-size-xs)",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        {" "}
                        -{" "}
                      </span>
                    )}
                  </div>
                </div>
                {expandedId === sheet.id && sheet.lines && (
                  <div
                    style={{
                      padding: "0 1.25rem 1rem 2.5rem",
                      background: "var(--color-bg)",
                    }}
                  >
                    {sheet.comment && (
                      <p
                        style={{
                          margin: "0 0 0.5rem",
                          fontSize: "var(--font-size-xs)",
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        Commentaire : « {sheet.comment} »
                      </p>
                    )}
                    {sheet.lines.map((line) => (
                      <div
                        key={line.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0.35rem 0",
                          fontSize: "var(--font-size-sm)",
                          borderTop: "1px solid var(--color-border-light)",
                        }}
                      >
                        <span>
                          {format(new Date(line.work_date), "EEE d MMM", {
                            locale: fr,
                          })}
                          {" - "}
                          {line.activity_label}
                          {line.description ? ` (${line.description})` : ""}
                        </span>
                        <strong>{line.hours} h</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>

      <Modal
        isOpen={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        title={
          reviewAction === "approve"
            ? "Valider la feuille de temps"
            : "Refuser la feuille de temps"
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setReviewTarget(null)}>
              Annuler
            </Button>
            <Button onClick={handleReview} disabled={reviewing}>
              {reviewing
                ? "Traitement…"
                : reviewAction === "approve"
                  ? "Valider"
                  : "Refuser"}
            </Button>
          </>
        }
      >
        {reviewTarget && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
              <strong>
                {reviewTarget.employee_name || reviewTarget.user_name}
              </strong>
              {" - "}
              {reviewTarget.week_label} ({reviewTarget.total_hours} h)
            </p>
            <textarea
              value={rhComment}
              onChange={(e) => setRhComment(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                fontSize: "var(--font-size-sm)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                fontFamily: "var(--font-family)",
              }}
              placeholder={
                reviewAction === "reject"
                  ? "Motif du refus…"
                  : "Commentaire RH…"
              }
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TimesheetsRhPage;
